interface ApiErrorDetail {
  error?: string;
  message?: string;
}

interface ValidationErrorDetail {
  msg?: string;
}

function detailMessage(detail: unknown): string | undefined {
  if (typeof detail === 'string' && detail.trim()) return detail.trim();
  if (Array.isArray(detail)) {
    const messages = detail
      .map((item) => detailMessage(item))
      .filter((message): message is string => Boolean(message));
    if (messages.length > 0) return messages.join(' ');
    return undefined;
  }
  if (!detail || typeof detail !== 'object') return undefined;

  const objectDetail = detail as Record<string, unknown>;
  for (const key of ['msg', 'message', 'error', 'detail']) {
    const message = detailMessage(objectDetail[key]);
    if (message) return message;
  }
  return detailMessage(objectDetail.errors);
}

/**
 * Extract a useful message from the error shapes returned by RTK Query,
 * FastAPI validation, and the playbook activation preflight.
 */
export function requestErrorMessage(
  error: unknown,
  fallback = 'The request could not be completed.'
): string {
  const candidate = error as {
    data?:
      | string
      | {
          detail?: string | ApiErrorDetail | ValidationErrorDetail[];
          message?: string;
          error?:
            | string
            | {
                code?: string;
                message?: string;
                details?: unknown;
              };
        };
    error?: string;
    message?: string;
  };

  if (typeof candidate?.data === 'string' && candidate.data.trim()) {
    return candidate.data;
  }

  const data = typeof candidate?.data === 'object' ? candidate.data : undefined;
  const detail = detailMessage(data?.detail);
  if (detail) return detail;

  const standardError =
    data?.error && typeof data.error === 'object' ? data.error : undefined;
  const standardDetails =
    standardError?.details && typeof standardError.details === 'object'
      ? (standardError.details as Record<string, unknown>)
      : undefined;
  const validationDetails = detailMessage(standardDetails?.errors);
  if (validationDetails) return validationDetails;
  const standardMessage = detailMessage(standardError?.message);
  if (standardMessage) return standardMessage;
  const remainingStandardDetails = detailMessage(standardError?.details);
  if (remainingStandardDetails) return remainingStandardDetails;
  const dataError = detailMessage(data?.error);
  if (dataError) return dataError;
  const dataMessage = detailMessage(data?.message);
  if (dataMessage) return dataMessage;
  const candidateError = detailMessage(candidate?.error);
  if (candidateError) return candidateError;
  const candidateMessage = detailMessage(candidate?.message);
  if (candidateMessage) return candidateMessage;
  return fallback;
}
